import React from 'react';
import { useSelector } from 'react-redux';
import { Permission, RootState } from 'v2/models';
import {
  getPermissionData,
  PERMISSION_NAME,
  isFreeSearcher,
  SEARCHER_PERMISSIONS,
} from 'v2/utils/permissions';
import { FEATURE_FLAGS } from 'utils/constants';
import { useFeatureFlags } from 'v2/hooks/useFeatureFlags';
import { DisabledCreateSearchButton } from './DisabledCreateSearchButton';
import { ActiveSearchButton } from './ActiveSearchButton';
import { useMyActiveSearchesCount } from 'v2/queries/searches/useMyActiveSearchesCount';
import { useMasterOrganization } from 'v2/queries';

interface CreateSearchButtonAttributes {
  featuredProviderMode?: boolean;
  featureProviderHandler?: () => void;
  disabled?: boolean;
}

const canCreateSearch = (
  searcherPermissions: Permission[],
  freeSearcherPermissions: Permission[],
  activeSearches: number,
  isFreeUser: boolean
) => {
  const isFreeUserWithAvailableSearches =
    isFreeUser &&
    freeSearcherPermissions[0]?.permission_quantity > activeSearches;

  return (
    isFreeUserWithAvailableSearches ||
    activeSearches < searcherPermissions[0]?.permission_quantity ||
    searcherPermissions[0]?.unlimited
  );
};

const getDisabledMessage = (searcherPermissions: Permission[]) => {
  if (searcherPermissions?.length > 1) {
    return 'Internal permission conflict. Please contact support.';
  }
  return `You have reached the maximum number of active requests allowed. If you would
  like to create a new request you must archive one of your active requests.`;
};

const CreateSearchButton = ({
  featuredProviderMode = false,
  featureProviderHandler,
  disabled = false,
}: CreateSearchButtonAttributes) => {
  const { data: masterOrganization } = useMasterOrganization();
  const [isFreeConnectEnabled] = useFeatureFlags([FEATURE_FLAGS.FREE_CONNECT]);
  const { data: activeSearches = 0 } = useMyActiveSearchesCount();
  const permissions = useSelector((state: RootState) => state.permissions);
  const permissionList = getPermissionData(
    permissions.permissions,
    Object.values(PERMISSION_NAME)
  );

  const isFreeUser =
    isFreeConnectEnabled && isFreeSearcher(permissions.permissions);
  const disabledByMasterOrg = masterOrganization?.unqualified && isFreeUser;
  const activePermissions = permissionList.filter(
    (permission: Permission) => permission.subscription_active
  );
  const freeSearcherPermissions = activePermissions.filter(
    (permission: Permission) =>
      [PERMISSION_NAME.CREATE_SEARCH_FREE].includes(permission.permission_name)
  );
  const searcherPermissions = activePermissions.filter(
    (permission: Permission) =>
      SEARCHER_PERMISSIONS.includes(permission.permission_name)
  );

  if (!permissions.loaded) {
    return null;
  }
  if (!activePermissions?.length) {
    return (
      <ActiveSearchButton label="Subscribe" link="/settings/subscription" />
    );
  }

  const isCreateSearchButtonDisabled = [
    searcherPermissions?.length > 1,
    activeSearches >= searcherPermissions[0]?.permission_quantity &&
      !searcherPermissions[0].unlimited,
  ].some(Boolean);

  if (isCreateSearchButtonDisabled) {
    return (
      <DisabledCreateSearchButton
        message={getDisabledMessage(searcherPermissions)}
      />
    );
  }
  if (
    canCreateSearch(
      searcherPermissions,
      freeSearcherPermissions,
      activeSearches,
      isFreeUser
    )
  ) {
    const activeButtonLabel = featuredProviderMode
      ? 'Request Data'
      : 'Create Request';
    return (
      <ActiveSearchButton
        label={activeButtonLabel}
        link="/create"
        asButton={featuredProviderMode}
        disabled={disabled || disabledByMasterOrg}
        onClickHandler={featureProviderHandler}
      />
    );
  }
  return null;
};

export default CreateSearchButton;
